import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useOrganizationService from '../../../app/services/hooks/useOrganizationService';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { AuthContext } from '../../../contexts/AuthContext';
import { hasPermission } from '../../../app/models/Auth';
import PageHeader from '../../../components/PageHeader/PageHeader';
import DefaultCard from '../../../components/default/DefaultCard';
import ButtonGeneral from '../../../components/buttons/ButtonGeneral';
import ButtonTableAction from '../../../components/buttons/ButtonTableAction';
import { contractLabel, formatDate } from '../types';
import { PortfolioClient } from './ClientList';

interface ClientBrand {
    id: number;
    application_number: string;
    brand_name: string;
    status: string;
    status_display: string;
    assigned_lawyer: string | null;
    created_at: string;
}

interface ClientContract {
    id: number;
    reference_code: string;
    contract_type: string;
    status: string;
    payment_status: string;
    signature_status: string;
    created_at: string;
}

const ClientDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const { auth } = useContext(AuthContext);
    const { requestConfirmation } = useReactConfirmAlert();
    const {
        showClient, archiveClient, restoreClient, resendClientWelcome, fetchingClient, fetchingClientAction
    } = useOrganizationService();

    const [client, setClient] = useState<PortfolioClient | null>(null);
    const [brands, setBrands] = useState<ClientBrand[]>([]);
    const [contracts, setContracts] = useState<ClientContract[]>([]);
    const [notFound, setNotFound] = useState(false);

    const active = auth?.organization?.status === 'active';
    const canCreateBrand = hasPermission(auth, 'add_brandregistrationapplication') && active;

    const load = () => {
        showClient(Number(id), {
            onSuccess: (response) => {
                setClient(response.data.client);
                setBrands(response.data.brand_applications);
                setContracts(response.data.contracts);
            },
            onError: () => setNotFound(true)
        });
    };

    useEffect(() => {
        load();
    }, [id]);

    const newBrandForClient = () => {
        if (!client) return;
        // Los datos van en el estado de navegación, no en la URL (no quedan en el
        // historial ni en los registros del servidor)
        history.push('/intranet/brand-registration/new', {
            prefill: {
                applicant_email: client.email,
                applicant_first_name: client.first_name,
                applicant_paternal_last_name: client.last_name,
                applicant_maternal_last_name: client.second_last_name
            }
        });
    };

    const toggleArchive = () => {
        if (!client) return;
        const archiving = client.status === 'active';
        requestConfirmation({
            title: archiving ? '¿Archivar cliente?' : '¿Reactivar cliente?',
            message: archiving
                ? 'Sale de tu cartera activa y libera un cupo de tu plan. Sus solicitudes y contratos se conservan.'
                : 'Vuelve a tu cartera activa y ocupa un cupo de tu plan.',
            buttons: {
                confirmButton: {
                    label: archiving ? 'Sí, archivar' : 'Sí, reactivar',
                    onClick: () => {
                        const action = archiving ? archiveClient : restoreClient;
                        action(client.id, {
                            onSuccess: (response) => {
                                toast.success(response.message);
                                load();
                            },
                            onError: (response) => toast.error(response.message)
                        });
                    }
                },
                cancelButton: { label: 'Cancelar' }
            }
        });
    };

    const resendWelcome = () => {
        if (!client) return;
        resendClientWelcome(client.id, {
            onSuccess: (response) => toast.success(response.message),
            onError: (response) => toast.error(response.message)
        });
    };

    if (notFound) {
        return <DefaultCard><p className="text-center text-muted py-5 mb-0">No se encontró el cliente en tu cartera.</p></DefaultCard>;
    }
    if (!client) {
        return <div className="text-center py-5">{fetchingClient && <div className="spinner-border text-primary" />}</div>;
    }

    return (
        <div className="container-fluid">
            <PageHeader
                breadcrumbs={[
                    { label: 'Inicio', path: '/intranet' },
                    { label: 'Cartera de Clientes', path: '/intranet/clients' },
                    { label: client.name, isCurrent: true }
                ]}
                title={client.name}
                description={`${client.email}${client.phone ? ` · ${client.phone}` : ''}${client.rut ? ` · RUT ${client.rut}` : ''}`}
            />

            <DefaultCard>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                        <span className={`badge ${client.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>{client.status_display}</span>
                        <span className={`badge ${client.account_active ? 'bg-success' : 'bg-warning text-dark'}`}>
                            Cuenta {client.account_active ? 'activa' : 'sin activar'}
                        </span>
                        <small className="text-muted">En cartera desde {formatDate(client.created_at)}{client.added_by ? ` · agregado por ${client.added_by}` : ''}</small>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        {canCreateBrand && client.status === 'active' && (
                            <ButtonGeneral onClick={newBrandForClient} title="Nueva solicitud de marca" icon="fa-plus" size="small" />
                        )}
                        {active && client.status === 'active' && hasPermission(auth, 'change_organizationclient') && (
                            <ButtonGeneral onClick={resendWelcome} title={client.account_active ? 'Reenviar acceso' : 'Reenviar activación'}
                                           variant="outline" size="small" icon="fa-paper-plane" loading={fetchingClientAction} />
                        )}
                        {hasPermission(auth, client.status === 'active' ? 'delete_organizationclient' : 'change_organizationclient') && (
                            <ButtonGeneral onClick={toggleArchive} title={client.status === 'active' ? 'Archivar' : 'Reactivar'}
                                           variant="outline" size="small" icon={client.status === 'active' ? 'fa-archive' : 'fa-box-open'} />
                        )}
                    </div>
                </div>
            </DefaultCard>

            <div className="row mt-3">
                <div className="col-lg-7 mb-3">
                    <DefaultCard>
                        <h5 className="mb-3">Solicitudes de marca ({brands.length})</h5>
                        <div className="table-responsive">
                            <table className="table table-hover align-middle">
                                <thead><tr><th>Marca</th><th>Estado</th><th>Abogado</th><th>Creada</th><th /></tr></thead>
                                <tbody>
                                    {brands.length === 0 ? (
                                        <tr><td colSpan={5} className="text-center text-muted py-3">Sin solicitudes.</td></tr>
                                    ) : brands.map((brand) => (
                                        <tr key={brand.id}>
                                            <td><span className="fw-bold d-block">{brand.brand_name || '—'}</span><small className="text-muted">{brand.application_number}</small></td>
                                            <td><span className="badge bg-light text-dark border">{brand.status_display}</span></td>
                                            <td><small>{brand.assigned_lawyer || <span className="text-warning">Sin asignar</span>}</small></td>
                                            <td><small>{formatDate(brand.created_at)}</small></td>
                                            <td className="text-center">
                                                <ButtonTableAction callbackFunction={() => history.push(`/intranet/brand-registration/stages/${brand.id}`)}
                                                                   title="Ver solicitud" classIcon="fa-eye" colorIcon="text-info" sizeIcon="16" />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </DefaultCard>
                </div>
                <div className="col-lg-5 mb-3">
                    <DefaultCard>
                        <h5 className="mb-3">Contratos ({contracts.length})</h5>
                        {contracts.length === 0 ? (
                            <p className="text-muted small mb-0">Sin contratos en tu estudio.</p>
                        ) : (
                            <ul className="list-group list-group-flush">
                                {contracts.map((contract) => (
                                    <li key={contract.id} className="list-group-item px-0">
                                        <span className="fw-bold d-block">{contract.contract_type}</span>
                                        <small className="text-muted">{contract.reference_code} · {contractLabel(contract.status)} · firma {contract.signature_status || 'pendiente'} · {formatDate(contract.created_at)}</small>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </DefaultCard>
                </div>
            </div>
        </div>
    );
};

export default ClientDetail;
